import { Button, Switch } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { ReactMultiEmail, isEmail } from 'react-multi-email'
import 'react-multi-email/style.css'
import styled from 'styled-components/macro'
import {
  readInvoiceForwardSettings,
  saveInvoiceForwardSettings,
  sendInvoiceForwardTestEmail,
  sendRecentInvoiceForwards,
} from 'src/utils/invoiceForwardApi'

const InvoiceForwardSettings = ({ venueId, onCaughtUp }) => {
  const [emails, setEmails] = useState([])
  const [savedEmails, setSavedEmails] = useState([])
  const [autoForward, setAutoForward] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  const [saving, setSaving] = useState(false)
  const [sendingTest, setSendingTest] = useState(false)
  const [sendingRecent, setSendingRecent] = useState(false)
  const [testNotice, setTestNotice] = useState('')
  const [open, setOpen] = useState(false)
  const sendingRef = useRef(false)

  useEffect(() => {
    if (!venueId) return undefined
    let cancelled = false
    readInvoiceForwardSettings(venueId)
      .then(data => {
        if (cancelled) return null
        setEmails(Array.isArray(data?.emails) ? data.emails : [])
        setSavedEmails(Array.isArray(data?.emails) ? data.emails : [])
        setAutoForward(data?.autoForward === true)
        return null
      })
      .catch(() => {
        if (!cancelled) setError('Forwarding settings could not be loaded.')
      })
    return () => {
      cancelled = true
    }
  }, [venueId])

  const save = async () => {
    setSaving(true)
    setError('')
    setSaved(false)
    try {
      const data = await saveInvoiceForwardSettings(venueId, {
        emails,
        autoForward,
      })
      setEmails(Array.isArray(data?.emails) ? data.emails : emails)
      setSavedEmails(Array.isArray(data?.emails) ? data.emails : [])
      setAutoForward(data?.autoForward === true)
      setSaved(true)
    } catch (err) {
      const apiErrors = err?.response?.data?.errors
      const message = err?.response?.data?.message
      setError(
        Array.isArray(apiErrors) && apiErrors[0]
          ? apiErrors[0]
          : message || 'Those settings could not be saved.',
      )
    } finally {
      setSaving(false)
    }
  }

  const sendTest = async () => {
    if (sendingRef.current || savedEmails.length === 0) return
    sendingRef.current = true
    setSendingTest(true)
    setTestNotice('')
    setError('')
    try {
      const result = await sendInvoiceForwardTestEmail(venueId)
      const to = Array.isArray(result?.to) ? result.to : savedEmails
      setTestNotice(`Sent to ${to.join(', ')}.`)
    } catch (err) {
      const status = err?.response?.status
      if (status === 429) {
        setTestNotice(
          `Sent to ${savedEmails.join(
            ', ',
          )}. Wait a minute before sending another.`,
        )
      } else {
        setError('The test email could not be sent.')
      }
    } finally {
      sendingRef.current = false
      setSendingTest(false)
    }
  }

  const summary =
    savedEmails.length === 0
      ? 'No addresses saved'
      : `${savedEmails.length} saved · auto-forward ${
          autoForward ? 'on' : 'off'
        }`

  return (
    <Card aria-label='Invoice forwarding'>
      <Header>
        <Toggle
          type='button'
          aria-expanded={open}
          onClick={() => setOpen(current => !current)}
        >
          <Title>Forward invoices</Title>
          <Note>
            {open
              ? 'Confirmed invoices can be sent to an accountant inbox such as Dext.'
              : summary}
          </Note>
        </Toggle>
        <HeaderActions>
          <Button
            type={open ? 'default' : 'primary'}
            onClick={() => setOpen(current => !current)}
          >
            {open ? 'Hide' : 'Set up'}
          </Button>
          {open ? (
            <>
              <Button
                disabled={savedEmails.length === 0 || sendingRecent}
                loading={sendingRecent}
                onClick={async () => {
                  setSendingRecent(true)
                  setError('')
                  setTestNotice('')
                  try {
                    const result = await sendRecentInvoiceForwards(venueId)
                    const sent = Number(result?.sent) || 0
                    setTestNotice(
                      sent
                        ? `Emailed ${sent} from the last 7 days.`
                        : 'Nothing from the last 7 days was waiting to be emailed.',
                    )
                    if (onCaughtUp) onCaughtUp()
                  } catch {
                    setError('Those invoices could not be emailed.')
                  } finally {
                    setSendingRecent(false)
                  }
                }}
              >
                Send the last 7 days
              </Button>
              <Button
                aria-label='Send a test'
                disabled={savedEmails.length === 0 || sendingTest}
                onClick={sendTest}
              >
                Send a test
              </Button>
            </>
          ) : null}
        </HeaderActions>
      </Header>
      {open ? (
        <>
          <Label>Email addresses</Label>
          <ReactMultiEmail
            placeholder='Enter email addresses'
            emails={emails}
            onChange={next => {
              setSaved(false)
              setEmails(next)
            }}
            validateEmail={email => isEmail(email)}
            getLabel={(email, index, removeEmail) => (
              <Chip data-tag key={index}>
                {email}
                <span data-tag-handle onClick={() => removeEmail(index)}>
                  ×
                </span>
              </Chip>
            )}
          />
          <SwitchBlock>
            <SwitchRow>
              <Switch
                checked={autoForward}
                aria-label='Auto-forward new invoices'
                onChange={checked => {
                  setSaved(false)
                  setAutoForward(checked)
                }}
              />
              <span>Auto-forward new invoices</span>
            </SwitchRow>
            <Disclaimer>Invoices already on this list are not sent.</Disclaimer>
          </SwitchBlock>
          {error ? <Error role='alert'>{error}</Error> : null}
          {testNotice ? <Notice role='status'>{testNotice}</Notice> : null}
          {saved ? (
            <Saved>✓ Saved</Saved>
          ) : (
            <Button type='primary' loading={saving} onClick={save}>
              Save
            </Button>
          )}
        </>
      ) : null}
      {error && !open ? <Error role='alert'>{error}</Error> : null}
    </Card>
  )
}

const Card = styled.section`
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  padding: 16px 24px;
  margin: 0 0 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`

const Toggle = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  text-align: left;
  cursor: pointer;
`

const Title = styled.h3`
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 600;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`

const Note = styled.p`
  margin: 0 0 16px;
  color: #6b6b6b;
  font-size: 13px;
`

const Label = styled.div`
  margin-bottom: 6px;
  font-weight: 500;
`

const Chip = styled.div`
  background-color: ${({ theme }) => theme.colours.lettuceGreen} !important;
`

const SwitchBlock = styled.div`
  margin: 16px 0;
`

const SwitchRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
`

const Disclaimer = styled.p`
  margin: 6px 0 0 0;
  color: #6b6b6b;
  font-size: 13px;
`

const Error = styled.p`
  color: #cf1322;
  margin: 0 0 8px;
`

const Notice = styled.p`
  margin: 0 0 8px;
`

const Saved = styled.span`
  color: #52c41a;
  font-weight: 500;
`

export default InvoiceForwardSettings
